/**
 * Goals on the one timeline (TIMELINE R26): a goal is met now, in the first month whose end meets
 * it, or not within the one 50-year horizon; progress is measured at now from the goal's start.
 * Not a contract function of its own: ForecastEngine.calculateForecast reports it, and the web app
 * and the MCP server read it from there.
 */
import { addDays, monthOf, yearOf, type Day } from '../core/calendar.ts';
import { abs, add, eq, gte, isZero, lte, max, sub, toDouble, ZERO, type Decimal } from '../core/decimal.ts';
import type { Position } from '../calculations/metrics.ts';
import { safeWithdrawalMonthlyIncome } from '../calculations/safe-withdrawal.ts';
import type { CumulativeForecast } from '../forecast/forecast-engine.ts';
import type { Goal, GoalSeries, Series } from '../types/domain.ts';
import type { GoalDeadline, GoalProgress } from './goal-tracker.ts';
import { monthsUntilMatch } from './goal-date-match.ts';
import { clampPercent } from './percent.ts';

/** R26: how far ahead every surface looks for a goal's date. */
export const GOAL_HORIZON_YEARS = 50;

export interface GoalLine {
  readonly now: Position;
  readonly today: Day;
  readonly safeWithdrawalRatePercent: Decimal;
  readonly cumulative: CumulativeForecast;
}

const positionValue = (p: Position, series: GoalSeries, safeWithdrawalRatePercent: Decimal): Decimal => {
  switch (series) {
    case 'netWorth':
      return p.netWorth;
    case 'cash':
      return p.cash;
    case 'investment':
      return p.investments;
    case 'debt':
      return p.debt;
    case 'passive':
      return safeWithdrawalMonthlyIncome(add(p.cash, p.investments), safeWithdrawalRatePercent);
  }
};

const meets = (goal: Goal, value: Decimal): boolean =>
  goal.operation === 'crossover' ? gte(value, goal.targetValue) : goal.operation === 'crossunder' ? lte(value, goal.targetValue) : eq(value, goal.targetValue);

const side = (value: Decimal, target: Decimal): number => (eq(value, target) ? 0 : gte(value, target) ? 1 : -1);

const isDebtFreeGoal = (goal: Goal): boolean => goal.series === 'debt' && goal.operation === 'crossunder' && isZero(goal.targetValue);

const sameMonth = (a: Day, b: Day): boolean => yearOf(a) === yearOf(b) && monthOf(a) === monthOf(b);

const calendarMonths = (from: Day, to: Day): number => (yearOf(to) - yearOf(from)) * 12 + (monthOf(to) - monthOf(from));

/** How far `value` is from meeting `goal`, 0 once it does. */
const shortfallOf = (goal: Goal, value: Decimal): Decimal =>
  meets(goal, value) ? ZERO : goal.operation === 'crossover' ? max(sub(goal.targetValue, value), ZERO) : goal.operation === 'crossunder' ? max(sub(value, goal.targetValue), ZERO) : abs(sub(goal.targetValue, value));

/** The goal's series at the end of `by`'s month: now when that month has passed, else the line's month end. */
function valueAtMonthEnd(series: Series, by: Day, today: Day, nowValue: Decimal): Decimal | null {
  if (calendarMonths(today, by) < 0) return nowValue;
  const bucket = series.find((b) => sameMonth(addDays(b.end, -1), by));
  return bucket === undefined ? null : bucket.amount;
}

/** R26 with a "by" day: on track when met by the by month, otherwise whole months behind, and the shortfall at that month's end. */
function deadlineOf(goal: Goal, by: Day, date: Day | null, achieved: boolean, line: GoalLine, longer: () => GoalLine, nowValue: Decimal): GoalDeadline {
  const month = achieved ? line.today : date;
  const onTrack = month !== null && calendarMonths(month, by) >= 0;
  const value = valueAtMonthEnd(line.cumulative[goal.series], by, line.today, nowValue) ?? valueAtMonthEnd(longer().cumulative[goal.series], by, line.today, nowValue) ?? nowValue;
  return {
    date: by,
    onTrack,
    monthsBehind: month === null ? null : Math.max(0, calendarMonths(by, month)),
    monthsAhead: month === null ? 0 : Math.max(0, calendarMonths(month, by)),
    valueAtDate: value,
    shortfall: onTrack ? ZERO : shortfallOf(goal, value),
  };
}

/** The last day of the first month whose end meets the goal (equal: hits or crosses it), or null. */
function firstMonthMeeting(goal: Goal, series: Series, nowValue: Decimal): Day | null {
  let previous = nowValue;
  for (const bucket of series) {
    const hit = goal.operation === 'equal' ? side(bucket.amount, goal.targetValue) !== side(previous, goal.targetValue) || eq(bucket.amount, goal.targetValue) : meets(goal, bucket.amount);
    if (hit) return addDays(bucket.end, -1);
    previous = bucket.amount;
  }
  return null;
}

/** Progress at now from the goal's start to its target, 0–100 to one decimal place; 100 once met. */
export function goalProgressAtNow(goal: Goal, nowValue: Decimal, setupValue: Decimal): number {
  if (meets(goal, nowValue)) return 100;
  const falling = goal.operation === 'crossunder';
  const start = toDouble(falling && isZero(goal.startingValue) ? setupValue : goal.startingValue);
  const target = toDouble(goal.targetValue);
  const current = toDouble(nowValue);
  const span = falling ? start - target : target - start;
  if (!(span > 0)) return 0;
  return Math.round(clampPercent(((falling ? start - current : current - start) / span) * 100) * 10) / 10;
}

/**
 * Every goal on the line. `extended` is the same line over the 50-year horizon, asked for only when
 * a goal is not met within `line`; `setup` is the position at the end of the setup day, the start of a
 * fall-to goal without a starting value.
 */
export function timelineGoals(
  goals: readonly Goal[],
  line: GoalLine,
  extended: () => GoalLine,
  setup: () => Position,
): readonly GoalProgress[] {
  let longer: GoalLine | null = null;
  return goals.map((goal): GoalProgress => {
    const nowValue = positionValue(line.now, goal.series, line.safeWithdrawalRatePercent);
    const achieved = meets(goal, nowValue);
    let date: Day | null = achieved ? line.today : firstMonthMeeting(goal, line.cumulative[goal.series], nowValue);
    if (date === null && !achieved) {
      longer ??= extended();
      date = firstMonthMeeting(goal, longer.cumulative[goal.series], nowValue);
    }
    const debtFree = (longer ?? line).cumulative.debtFreeDate;
    if (date !== null && !achieved && isDebtFreeGoal(goal) && debtFree !== null && sameMonth(debtFree, date)) date = debtFree;
    const needsSetup = goal.operation === 'crossunder' && isZero(goal.startingValue) && !achieved;
    const by = goal.targetDate ?? null;
    return {
      goal,
      currentValue: nowValue,
      targetValue: goal.targetValue,
      progressPercent: goalProgressAtNow(goal, nowValue, needsSetup ? positionValue(setup(), goal.series, line.safeWithdrawalRatePercent) : nowValue),
      estimatedDate: date,
      monthsRemaining: achieved ? 0 : monthsUntilMatch(date, line.today),
      isAchieved: achieved,
      ...(by === null ? {} : { deadline: deadlineOf(goal, by, date, achieved, line, () => (longer ??= extended()), nowValue) }),
    };
  });
}
